import { C, type Hex, mix, type Pix } from './pix';

/**
 * Where icons are kept. Each kind (moves, arts, neigong books, statuses, items…) is described
 * once with `defineIconKind`; files under `icons/<kind>/` add hand-made pictures with
 * `registerIcon`, and `registry.ts` collects every such file, so a new move, book or item brings
 * its own icon file and nothing else changes. An id with no hand-made picture is drawn by its
 * kind's generator.
 */

export type IconKind =
  | 'skill'
  | 'art'
  | 'neigong'
  | 'qinggong'
  | 'status'
  | 'item'
  | 'good'
  | 'school'
  | 'badge'
  | 'profession'
  | 'mount'
  | 'title'
  | 'spot'
  | 'action'
  | 'currency'
  | 'element';

/** Icons are drawn 32×32 art pixels, with a 16×16 version for small slots. */
export type IconSize = 16 | 32;

/**
 * The mark of a move's kind, the same on every icon:
 * - `attack` (攻) a clean tile;
 * - `break` (破) a seal-red corner seal with a paper crack, bottom right;
 * - `power` (怒) a full seal-red frame with a paper inner line;
 * - `heal` / `buff` (療) a paper tab top left with a jade cross / rising chevron;
 * - `guard` (守) a paper tab top left with a steel shield;
 * - `debuff` a seal-red border (statuses).
 */
export type Marker = 'attack' | 'break' | 'power' | 'heal' | 'buff' | 'guard' | 'debuff' | 'none';

/** The ground an icon sits on: a bevelled tile, a round medallion, or none (the shape is its own). */
export type Plate = 'square' | 'round' | 'none';

/** One colour in three cel tones, plus a deep shade for outlines and a near-white highlight. */
export interface Pal {
  c: Hex;
  l: Hex;
  d: Hex;
  dd: Hex;
  hi: Hex;
}

export const pal = (c: Hex): Pal => ({
  c,
  l: mix(c, C.white, 0.45),
  d: mix(c, C.ink, 0.42),
  dd: mix(c, C.ink, 0.78),
  hi: mix(c, C.white, 0.8),
});

export interface IconCtx {
  readonly id: string;
  readonly size: IconSize;
  /** The icon's tint (an art's element, an item's rarity…) in tones. */
  readonly p: Pal;
}

export type Draw = (p: Pix, c: IconCtx) => void;

export interface IconArt {
  /** Draws the picture on a transparent buffer at the icon's native size (32 unless `native`). */
  readonly draw: Draw;
  /** A hand-drawn 16×16 version; without one the 32 picture is halved. */
  readonly draw16?: Draw;
  /** 16: drawn at 16 and doubled for 32 (statuses). */
  readonly native?: IconSize;
  readonly tint?: Hex;
  readonly plate?: Plate;
  readonly marker?: Marker;
  /** Leaves the picture's own colours alone (no cel light pass). */
  readonly flat?: boolean;
}

export interface KindDef {
  /** The kind's name in Thai (review sheets). */
  readonly label: string;
  /** Every id the game has of this kind. */
  ids(): readonly string[];
  /** An id's name in Thai. */
  name(id: string): string;
  tint(id: string): Hex;
  readonly plate: Plate;
  marker?(id: string): Marker;
  /** The generator for ids with no hand-made picture. */
  fallback(id: string): IconArt;
  readonly native?: IconSize;
}

const KINDS = new Map<IconKind, KindDef>();
const ART = new Map<string, IconArt>();
const key = (kind: IconKind, id: string) => `${kind}:${id}`;

export function defineIconKind(kind: IconKind, def: KindDef): void {
  KINDS.set(kind, def);
}

/** Adds a hand-made picture for one id of a kind. */
export function registerIcon(kind: IconKind, id: string, art: IconArt | Draw): void {
  ART.set(key(kind, id), typeof art === 'function' ? { draw: art } : art);
}

/** Several pictures of one kind at once, by id. */
export function registerIcons(kind: IconKind, arts: Readonly<Record<string, IconArt | Draw>>): void {
  for (const [id, art] of Object.entries(arts)) registerIcon(kind, id, art);
}

export const kindDef = (kind: IconKind): KindDef | undefined => KINDS.get(kind);
export const iconKinds = (): IconKind[] => [...KINDS.keys()];
export const handMade = (kind: IconKind, id: string): boolean => ART.has(key(kind, id));
export const handMadeArt = (kind: IconKind, id: string): IconArt | undefined => ART.get(key(kind, id));
